import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import DropdownMenu, { DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../components/ui/DropdownMenu";
import Header from "../../components/ui/Header";
import GridSection from "../../components/ui/sections/GridSection";
import { useCategories } from "../../hooks/useCategories";
import { useListPost } from "../../hooks/useListPost";
import { ButtonNavigationInterface } from "../../types/dashboard.type";

const ListPage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const {
    data,
    sortby,
  }: ButtonNavigationInterface = location.state || {};

  const { api, posttype, type: typeData } = data || {}; // Retrieve the new api prop from state
   
  const [sortBy, setSortBy] = React.useState<string>(
    sortby || "views_day__desc"
  );
 
  const { data: categories } = useCategories(posttype || "movie");
  
  const [selectedFilter, setSelectedFilter] = useState<{
    id: number;
    type: string;
    label: string;
    slug: string;
  } | undefined>(
    typeData === "category" && location.state?.category ? {
      ...location.state.category,
    } : undefined
  ); // State for selected filter

  // Construct API endpoint with category if selected
  const apiEndpoint = React.useMemo(() => {
    if (!api) return '';
    
    // Remove trailing slash if present
    const baseApi = api.endsWith('/') ? api.slice(0, -1) : api;
    
    // Make sure we have both api and selectedFilter before constructing the category URL
    if (selectedFilter && selectedFilter.slug) {
      // Format: https://phim3s.net/vi/api/v1/posts/lists/movie/chinh-kich
      // The final URL after params: https://phim3s.net/vi/api/v1/posts/lists/movie/chinh-kich/?sortby=views_day__desc&paged=1
      return `${baseApi}/${selectedFilter.slug}`;
    }
    
    // Fall back to the original API if no filter is selected
    return baseApi;
  }, [api, selectedFilter]);

  // Use the updated useListPost without paged in the initial params
  const {
    posts,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useListPost(apiEndpoint, posttype || "movie", { sortby: sortBy });

  // Fetch next page on scroll to bottom
  useEffect(() => {
    const onScroll = () => {
      const scrollPosition = window.innerHeight + document.documentElement.scrollTop;
      const scrollThreshold = document.documentElement.offsetHeight - 200;
      if (
        scrollPosition >= scrollThreshold &&
        hasNextPage &&
        !isFetchingNextPage
      ) {
        fetchNextPage();
      }
    };
    let scrollTimeout: ReturnType<typeof setTimeout>;
    const debouncedOnScroll = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(onScroll, 100);
    };
    window.addEventListener("scroll", debouncedOnScroll);
    return () => {
      window.removeEventListener("scroll", debouncedOnScroll);
      clearTimeout(scrollTimeout);
    };
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  // Scroll to top on filter or sort change
  useEffect(() => {
    window.scrollTo(0, 0);
    // Reset scroll position when filters change
  }, [apiEndpoint, sortBy, selectedFilter]);

  // We don't need a separate useEffect to call refetch anymore 
  // The useQuery hook will automatically refetch when queryKey values change
  useEffect(() => {
    // Log when sort or filter changes to better track user interactions
    console.log("Sort or filter changed:", { 
      sortBy, 
      selectedCategory: selectedFilter?.label || "None" 
    });
  }, [sortBy, selectedFilter]);

  console.log("data", location.state);
  console.log("API Endpoint:", apiEndpoint, "Sort by:", sortBy);
  
  // Example of the expected URL format for debugging
  const expectedUrlExample = `${apiEndpoint}/?sortby=${sortBy}&paged=1`;
  console.log("Expected final URL format:", expectedUrlExample);
  const menuSort = [
    {
      key: "views_day__desc",
      value: t("view_list.trending"),
    },
    {
      key: "created_at__desc",
      value: t("view_list.newest"),
    },
    {
      key: "updated_at__desc",
      value: t("view_list.last_update"),
    },
    {
      key: "like_count__desc",
      value: t("view_list.likes"),
    },
    {
      key: "views_week__desc",
      value: t("view_list.views"),
    },
    {
      key: "rating_total__desc",
      value: t("view_list.rating"),
    },
  ];

  // Show loading state when initially loading
  if (isLoading && !posts) return <div>Loading...</div>;
  if (error) return <div>Error loading categories</div>;
  
  const handleTabClick = (tab: any) => {
    // Log the tab information for debugging
    console.log("Tab clicked:", tab);
    
    // Update selected filter when clicking a category tab
    // If the currently selected tab is clicked again, unselect it (reset filter)
    if (selectedFilter && selectedFilter.id === tab.id) {
      setSelectedFilter(undefined);
      console.log("Unselected category filter");
    } else {
      setSelectedFilter({
        id: tab.id,
        type: tab.type || "category",
        label: tab.name || tab.label,
        slug: tab.slug
      });
      console.log("Selected category with slug:", tab.slug);
    }
  };
  
  // Handle sort selection
  const handleSortSelect = (sortKey: string) => {
    setSortBy(sortKey);
  };
  
  return (
    <div>
      <Header
      className="bg-mainBlack"
      isBack
        tabs={
          categories?.map((item: any) => {
            return {
              ...item,
              label: item.name,
            };
          }) || []
        }
        onTabClick={handleTabClick}
        selectedFilter={selectedFilter}
        renderBottomNav={
          <div className="relative flex items-center space-x-2 justify-between px-4 py-4">
            {/* Display current selected filter value */}
            <div className="text-white text-[24px] font-semibold">
              {menuSort.find(item => item.key === sortBy)?.value || t("view_list.filter")}
            </div>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild toggleMenu={() => {}}>
                <button className="text-white flex items-center gap-2">
                 
                  <img src="/assets/icons/filter.svg" alt="Filter" className="w-6 h-6" />
                   <span className="mr-1">Lọc</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent isOpen={true} align="end" className="w-56 bg-gray-900 border-gray-700 text-white">
                {menuSort.map((item) => (
                  <DropdownMenuItem 
                    key={item.key} 
                    onClick={() => handleSortSelect(item.key)}
                  >
                    <div className={`flex items-center ${sortBy === item.key ? 'font-bold text-blue-400' : ''}`}>
                      {item.value}
                      {sortBy === item.key && (
                        <span className="ml-2">✓</span>
                      )}
                    </div>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            
          </div>
        }
      />
      {/* Infinite scroll section */}
      <GridSection movies={posts} />
      {/* Initial loading indicator */}
      {isLoading && posts.length === 0 && (
        <div className="p-4 text-center flex flex-col items-center justify-center">
          <div className="w-8 h-8 border-t-2 border-b-2 border-blue-500 rounded-full animate-spin mb-2"></div>
          <p>Đang tải dữ liệu...</p>
        </div>
      )}
      {/* Loading more indicator */}
      {isFetchingNextPage && (
        <div className="p-4 text-center flex flex-col items-center justify-center transition-all duration-300">
          <div className="w-8 h-8 border-t-2 border-b-2 border-blue-500 rounded-full animate-spin mb-2"></div>
          <p>Đang tải thêm...</p>
        </div>
      )}
    </div>
  );
};

export default ListPage;
